(() => {
  if (window.msCardQuickBuyReady) return;
  window.msCardQuickBuyReady = true;

  const productCache = new Map();

  const escapeHtml = (value) =>
    String(value || '').replace(/[&<>"']/g, (char) => ({
      '&': '&amp;',
      '<': '&lt;',
      '>': '&gt;',
      '"': '&quot;',
      "'": '&#039;',
    })[char]);

  const fetchProduct = async (handle) => {
    if (!productCache.has(handle)) {
      const shopRoot = window.Shopify?.routes?.root || '/';
      const productUrl = `${shopRoot.replace(/\/?$/, '/')}products/${encodeURIComponent(handle)}.js`;
      productCache.set(
        handle,
        fetch(productUrl, { headers: { Accept: 'application/json' } }).then(
          (response) => {
            if (!response.ok) throw new Error('Produto não encontrado.');
            return response.json();
          }
        )
      );
    }

    return productCache.get(handle);
  };

  const getOptionNames = (product) =>
    (product.options || []).map((option) => String(option?.name || option || '').toLowerCase());

  const findSizeIndex = (product) => {
    const names = getOptionNames(product);
    const namedIndex = names.findIndex((name) => name.includes('tamanho') || name.includes('size'));
    if (namedIndex !== -1) return namedIndex;

    const optionCount = Math.max(...product.variants.map((variant) => variant.options?.length || 0), 0);
    for (let index = 0; index < optionCount; index += 1) {
      const values = [...new Set(product.variants.map((variant) => String(variant.options?.[index] || '')))].filter(Boolean);
      const numericValues = values.filter((value) => /\d/.test(value));
      if (values.length > 1 && numericValues.length >= Math.ceil(values.length * 0.6)) return index;
    }

    return -1;
  };

  const hydrateQuickBuy = async (quickBuy) => {
    if (quickBuy.dataset.msHydrated === 'true') return;
    quickBuy.dataset.msHydrated = 'true';

    try {
      const product = await fetchProduct(quickBuy.dataset.productHandle);
      const sizeIndex = findSizeIndex(product);
      const titleButton = quickBuy.querySelector('[data-ms-card-quick-buy-toggle]');

      if (sizeIndex === -1) {
        const variant = product.variants.find((item) => item.available) || product.variants[0];
        if (!variant || !variant.available || !titleButton) return;

        titleButton.textContent = 'Adicionar ao carrinho';
        titleButton.removeAttribute('data-ms-card-quick-buy-toggle');
        titleButton.removeAttribute('aria-expanded');
        titleButton.setAttribute('data-ms-direct-add', '');
        titleButton.setAttribute('data-variant-id', escapeHtml(variant.id));
        titleButton.setAttribute('aria-label', 'Adicionar ao carrinho');
        quickBuy.dataset.msReady = 'true';
        quickBuy.dataset.msDirect = 'true';
        return;
      }

      const variantsBySize = new Map();
      product.variants.forEach((variant) => {
        const size = String(variant.options?.[sizeIndex] || '');
        if (variant.available && size && !variantsBySize.has(size)) variantsBySize.set(size, variant);
      });
      if (!variantsBySize.size) return;

      const sizes = quickBuy.querySelector('[data-ms-card-quick-buy-sizes]');
      sizes.innerHTML = [...variantsBySize.entries()]
        .map(
          ([size, variant]) => `
            <button
              type="button"
              class="ms-card-quick-buy__size"
              data-ms-card-quick-buy-size
              data-variant-id="${escapeHtml(variant.id)}"
              aria-label="Adicionar tamanho ${escapeHtml(size)}"
            >${escapeHtml(size)}</button>
          `
        )
        .join('');
      quickBuy.dataset.msReady = 'true';
    } catch (error) {
      quickBuy.dataset.msHydrated = 'false';
      const status = quickBuy.querySelector('[data-ms-card-quick-buy-status]');
      if (status) status.textContent = 'Não foi possível carregar os tamanhos.';
    }
  };

  const hydrateAll = (root = document) => {
    root.querySelectorAll?.('[data-ms-card-quick-buy]').forEach(hydrateQuickBuy);
  };

  const addVariant = async (button) => {
    const quickBuy = button.closest('[data-ms-card-quick-buy]');
    const status = quickBuy?.querySelector('[data-ms-card-quick-buy-status]');
    if (!quickBuy || button.disabled) return;

    button.disabled = true;
    if (status) status.textContent = 'A adicionar...';

    try {
      const cartUi = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
      const formData = new FormData();
      formData.append('id', button.dataset.variantId);
      formData.append('quantity', '1');

      if (cartUi) {
        formData.append(
          'sections',
          cartUi
            .getSectionsToRender()
            .map((section) => section.id)
            .join(',')
        );
        formData.append('sections_url', window.location.pathname);
        if (cartUi.setActiveElement) cartUi.setActiveElement(button);
      }

      const response = await fetch(window.routes.cart_add_url, {
        method: 'POST',
        headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: formData,
      });
      const result = await response.json();
      if (!response.ok || result.status) {
        throw new Error(result.description || result.message || 'Não foi possível adicionar este tamanho.');
      }

      if (cartUi && result.sections) cartUi.renderContents(result);
      if (status) status.textContent = `Tamanho ${button.textContent.trim()} adicionado.`;

      document.dispatchEvent(
        new CustomEvent('ms:footwear-added', {
          detail: {
            eligible: quickBuy.dataset.msFootwear === 'true',
            trigger: button,
          },
        })
      );
    } catch (error) {
      if (status) status.textContent = error.message || 'Não foi possível adicionar.';
    } finally {
      button.disabled = false;
    }
  };

  const addDirectVariant = async (button) => {
    const quickBuy = button.closest('.ms-card-quick-buy');
    const variantId = button.dataset.variantId;
    if (!quickBuy || !variantId || button.disabled) return;

    const originalText = button.textContent;
    button.disabled = true;
    button.textContent = 'A adicionar...';

    try {
      const cartUi = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
      const formData = new FormData();
      formData.append('id', variantId);
      formData.append('quantity', '1');

      if (cartUi) {
        formData.append(
          'sections',
          cartUi
            .getSectionsToRender()
            .map((section) => section.id)
            .join(',')
        );
        formData.append('sections_url', window.location.pathname);
        if (cartUi.setActiveElement) cartUi.setActiveElement(button);
      }

      const response = await fetch(window.routes.cart_add_url, {
        method: 'POST',
        headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: formData,
      });
      const result = await response.json();
      if (!response.ok || result.status) {
        throw new Error(result.description || result.message || 'Nao foi possivel adicionar este produto.');
      }

      if (cartUi && result.sections) cartUi.renderContents(result);
      document.dispatchEvent(
        new CustomEvent('ms:footwear-added', {
          detail: {
            eligible: quickBuy.dataset.msFootwear === 'true',
            trigger: button,
          },
        })
      );
      button.textContent = 'Adicionado';
      window.setTimeout(() => {
        button.textContent = originalText;
      }, 1600);
    } catch (error) {
      button.textContent = error.message || 'Tente novamente';
      window.setTimeout(() => {
        button.textContent = originalText;
      }, 2200);
    } finally {
      button.disabled = false;
    }
  };

  document.addEventListener('click', (event) => {
    const toggle = event.target.closest('[data-ms-card-quick-buy-toggle]');
    if (toggle) {
      const quickBuy = toggle.closest('[data-ms-card-quick-buy]');
      const isOpen = quickBuy?.dataset.msOpen === 'true';
      if (quickBuy) quickBuy.dataset.msOpen = isOpen ? 'false' : 'true';
      toggle.setAttribute('aria-expanded', String(!isOpen));
      event.preventDefault();
      event.stopPropagation();
      return;
    }

    const directButton = event.target.closest('[data-ms-direct-add]');
    if (directButton) {
      event.preventDefault();
      event.stopPropagation();
      addDirectVariant(directButton);
      return;
    }

    const button = event.target.closest('[data-ms-card-quick-buy-size]');
    if (!button) return;

    event.preventDefault();
    event.stopPropagation();
    addVariant(button);
  });

  document.addEventListener('mouseover', (event) => {
    const quickBuy = event.target.closest('[data-ms-card-quick-buy]');
    if (!quickBuy || window.matchMedia('(max-width: 749px)').matches) return;
    quickBuy.dataset.msOpen = 'true';
    quickBuy.querySelector('[data-ms-card-quick-buy-toggle]')?.setAttribute('aria-expanded', 'true');
  });

  document.addEventListener('focusin', (event) => {
    const quickBuy = event.target.closest('[data-ms-card-quick-buy]');
    if (!quickBuy || window.matchMedia('(max-width: 749px)').matches) return;
    quickBuy.dataset.msOpen = 'true';
    quickBuy.querySelector('[data-ms-card-quick-buy-toggle]')?.setAttribute('aria-expanded', 'true');
  });

  const observer = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      mutation.addedNodes.forEach((node) => {
        if (node.nodeType !== 1) return;
        if (node.matches?.('[data-ms-card-quick-buy]')) hydrateQuickBuy(node);
        hydrateAll(node);
      });
    });
  });

  window.msHydrateQuickBuy = hydrateAll;
  hydrateAll();
  observer.observe(document.body, { childList: true, subtree: true });
})();
